import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import {
  K8sConfigurationOptionsInput,
  K8sScanNodesStyle,
  useGetClientIntegrationQuery,
  useGetClientIntegrationTokenQuery,
} from "~/operations";
import appsData from "~/pages/integrations/IntegrationsSetup/utils/integration-setup.json";

import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { Home as HomeIcon } from "@mui/icons-material";
import { K8OperatorConfiguration } from "~/pages/integrations/kubernetes/K8OperatorConfiguration/K8OperatorConfiguration";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export type UpdateK8Data = {
  name: string;
  configurationOptions: K8sConfigurationOptionsInput;
  token: string;
  handleUpdate: (configOptions: K8sConfigurationOptionsInput) => Promise<void>;
};

export interface K8UpdateConfigurationProps {
  scope: SpaceScope | OrganizationScope;
}

export const K8UpdateConfigurationPage = ({
  scope,
}: K8UpdateConfigurationProps) => {
  const { integrationId } = useParams();
  const integrationMrn = getIntegrationMrn({
    scope,
    integrationId,
  });

  const { data, loading, error } = useGetClientIntegrationQuery({
    variables: { mrn: integrationMrn },
  });

  const {
    data: tokenData,
    loading: tokenLoading,
    error: tokenError,
  } = useGetClientIntegrationTokenQuery({
    variables: { input: { mrn: integrationMrn } },
  });

  const integration = data?.clientIntegration.integration;

  setDocumentTitle([
    "Edit",
    integration?.name,
    "Kubernetes",
    "Integrations",
    scope.name,
  ]);

  if (loading || tokenLoading) {
    return <LoadingPage />;
  }

  if (
    error ||
    tokenError ||
    !tokenData ||
    !integrationId ||
    integration?.configurationOptions?.__typename !== "K8sConfigurationOptions"
  ) {
    return <LoadingFailedPage />;
  }

  const { name, configurationOptions } = integration;
  const { token } = tokenData.getClientIntegrationToken;

  // configurationOptions is coming through as the type of agent instead of K8s
  const defaultValues = {
    integrationName: name,
    scanNodes: configurationOptions?.scanNodes || false,
    scanNodesStyle:
      configurationOptions?.scanNodesStyle || K8sScanNodesStyle.Cronjob,

    // workload scanning section
    scanWorkloads: configurationOptions?.scanWorkloads || false,
    workloadImageScanning: configurationOptions?.scanPublicImages || false,
    namespaceScanning: false,
    namespaceAllowList:
      configurationOptions?.namespaceAllowList?.join(", ") || "",
    namespaceDenyList:
      configurationOptions?.namespaceDenyList?.join(", ") || "",

    // scan incoming deployments section
    scanDeploys: configurationOptions?.scanDeploys || false,
    certificateManager: configurationOptions?.certificateManager || "",
  };

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations/kubernetes"
      component={RouterLink}
      to={`/space/integrations/kubernetes?${scope.params}`}
    >
      Kubernetes
    </Link>,
    <Link
      key="/space/integrations/kubernetes/integration"
      component={RouterLink}
      to={`/space/integrations/kubernetes/${integrationId}?${scope.params}`}
    >
      {name}
    </Link>,
    <Typography key={1}>Operator Configuration</Typography>,
  ];

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <K8OperatorConfiguration
        scope={scope}
        setup={appsData["mondoo/kubernetes"]}
        updateFlow={{
          values: defaultValues,
          integrationMrn,
          integrationId,
          token,
        }}
      />
    </Box>
  );
};
